import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, CaretDown, Check } from "@phosphor-icons/react";
import { LANGUAGES } from "@/i18n";

export default function LanguageSwitcher({ variant = "navbar" }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current =
    LANGUAGES.find((l) => l.code === i18n.language) ||
    LANGUAGES.find((l) => i18n.language?.startsWith(l.code)) ||
    LANGUAGES[0];

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const change = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        type="button"
        aria-label={t("nav.language")}
        onClick={() => setOpen((o) => !o)}
        data-testid="language-switcher-toggle"
        className={`inline-flex items-center gap-2 text-xs uppercase tracking-wider border border-white/15 hover:border-[#E6C998]/60 text-neutral-300 hover:text-white px-3 py-2 transition-colors ${
          variant === "footer" ? "bg-transparent" : "bg-black/40"
        }`}
      >
        <Globe size={14} weight="duotone" className="text-[#E6C998]" />
        <span>{current.code.toUpperCase()}</span>
        <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="language-switcher-menu"
          className={`absolute z-50 min-w-[190px] mt-2 bg-[#0A0A0A] border border-[#E6C998]/25 shadow-2xl py-2 ${
            variant === "footer" ? "bottom-full mb-2" : "right-0"
          }`}
        >
          {LANGUAGES.map((l) => {
            const active = l.code === current.code;
            return (
              <button
                key={l.code}
                type="button"
                data-testid={`language-option-${l.code}`}
                onClick={() => change(l.code)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-2 text-sm transition-colors ${
                  active ? "text-[#E6C998] bg-white/5" : "text-neutral-200 hover:bg-white/5 hover:text-white"
                }`}
                dir={l.dir}
              >
                <span className="flex items-center gap-2">
                  <span className="text-base leading-none">{l.flag}</span>
                  <span>{l.native}</span>
                </span>
                {active && <Check size={14} weight="bold" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
